import { Box, IconButton, Typography } from '@material-ui/core';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { CloudUpload, InsertDriveFileOutlined, Visibility } from '@material-ui/icons';
import { useDocumentStore } from '@roc/feature-documents';
import { Document } from '@roc/feature-types';
import { useDropzone } from 'react-dropzone';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    row: {
      display: 'flex',
      alignItems: 'center',
      backgroundColor: '#FAFAFA',
      borderRadius: 8,
      border: '1px solid #E0E0E0',
      padding: theme.spacing(1.5, 2),
      gap: theme.spacing(1),
    },
    fileIconWrapper: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 32,
      height: 32,
      borderRadius: 6,
      backgroundColor: '#E8EFFE',
      flexShrink: 0,
    },
    fileIcon: {
      color: '#7B9FE8',
      fontSize: '1.1rem',
    },
    fileName: {
      flexGrow: 1,
      fontSize: '0.875rem',
      fontWeight: 700,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    actionWrapper: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 32,
      height: 32,
      borderRadius: 6,
      border: '1px solid #E0E0E0',
      backgroundColor: '#FFFFFF',
      flexShrink: 0,
    },
    actionButton: {
      padding: 0,
      width: '100%',
      height: '100%',
      borderRadius: 6,
    },
    viewIcon: {
      color: '#7B9FE8',
    },
    expandedContainer: {
      border: '1px solid #E0E0E0',
      borderRadius: 8,
      overflow: 'hidden',
    },
    fileRow: {
      display: 'flex',
      alignItems: 'center',
      backgroundColor: '#FAFAFA',
      padding: theme.spacing(1.5, 2),
      gap: theme.spacing(1),
    },
    reuploadArea: {
      borderTop: '1px dashed #E0E0E0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing(1),
      padding: theme.spacing(1.5),
      cursor: 'pointer',
      color: '#427ED1',
    },
    reuploadAreaDisabled: {
      cursor: 'not-allowed',
      color: '#B0B0B0',
      opacity: 0.6,
    },
  })
);

export interface UploadedDocumentProps {
  document: Document;
  onUpload?: (file: File) => void;
  disabled?: boolean;
  // Opens the file; defaults to the task document preview. For files not stored as task documents.
  onView?: () => void;
}

export const UploadedDocument = ({
  document,
  onUpload,
  disabled,
  onView,
}: UploadedDocumentProps) => {
  const classes = useStyles();
  const { documentStore } = useDocumentStore();

  const onDrop = (acceptedFiles: File[]) => {
    if (acceptedFiles[0]) onUpload?.(acceptedFiles[0]);
  };

  const { getRootProps, getInputProps } = useDropzone({ onDrop, multiple: false, disabled });

  if (!document) return null;

  const fileRowContent = (
    <>
      <Box className={classes.fileIconWrapper}>
        <InsertDriveFileOutlined className={classes.fileIcon} />
      </Box>
      <Typography className={classes.fileName}>
        {document?.originalFileName}
      </Typography>
      <Box className={classes.actionWrapper}>
        <IconButton
          className={classes.actionButton}
          onClick={() => (onView ? onView() : documentStore.previewDocument(document))}
          size="small"
        >
          <Visibility fontSize="small" className={classes.viewIcon} />
        </IconButton>
      </Box>
    </>
  );

  if (onUpload) {
    return (
      <Box className={classes.expandedContainer}>
        <Box className={classes.fileRow}>
          {fileRowContent}
        </Box>
        <Box
          className={`${classes.reuploadArea} ${disabled ? classes.reuploadAreaDisabled : ''}`}
          {...getRootProps()}
        >
          <input {...getInputProps()} />
          <CloudUpload style={{ fontSize: '1.1rem' }} />
          <Typography style={{ fontSize: '0.875rem' }}>Re-upload file</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.row}>
      {fileRowContent}
    </Box>
  );
};
